import React, { useRef, useState } from 'react';
import { ICON } from '../../icons/iconIds';
import IconButton from '../icons/IconButton';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { documentDesignService } from '../../services/documentDesignService';
import { formatBytes } from './slotFitness';

const ACCEPT = '.jpg,.jpeg,.png,.webp,.tif,.tiff,.bmp,.gif,.svg';
const MAX_BYTES = 90 * 1024 * 1024;

const MediaUploadModal = ({ folders, defaultFolder, onClose, onUploaded }) => {
  const [folder, setFolder] = useState(defaultFolder || folders[0]?.path || '');
  const [files, setFiles] = useState([]);
  const [approved, setApproved] = useState(false);
  const [credit, setCredit] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const addFiles = (list) => {
    const incoming = [...list].map((file) => ({
      file,
      status: file.size > MAX_BYTES ? 'error' : 'queued',
      message: file.size > MAX_BYTES ? 'Larger than 90 MB' : '',
    }));
    setFiles((prev) => [...prev, ...incoming]);
  };

  const updateEntry = (index, patch) =>
    setFiles((prev) => prev.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)));

  const upload = async () => {
    setUploading(true);
    let uploadedCount = 0;
    for (let i = 0; i < files.length; i += 1) {
      if (files[i].status !== 'queued') continue;
      updateEntry(i, { status: 'uploading', message: '' });
      try {
        const asset = await documentDesignService.uploadMedia(files[i].file, folder);
        if (approved || credit.trim()) {
          await documentDesignService.updateMedia(asset.id, {
            ...(approved ? { approved_external: true } : {}),
            ...(credit.trim() ? { photo_credit: credit.trim() } : {}),
          });
        }
        updateEntry(i, { status: 'done', message: asset.sync_status === 'ready' ? 'Added' : asset.sync_status });
        uploadedCount += 1;
      } catch (err) {
        updateEntry(i, { status: 'error', message: err.message });
      }
    }
    setUploading(false);
    if (uploadedCount > 0) onUploaded?.();
  };

  const queued = files.filter((f) => f.status === 'queued').length;

  return (
    <div className="dd-modal-overlay" onClick={uploading ? undefined : onClose} role="presentation">
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Upload photos">
        <Card className="dd-modal">
          <div className="dd-detail-header">
            <h3>Upload to Brand Library</h3>
            <IconButton icon={ICON.ddClose} onClick={onClose} ariaLabel="Close" disabled={uploading} />
          </div>

          <div className="dd-form">
            <label>
              Folder
              <select value={folder} onChange={(e) => setFolder(e.target.value)} disabled={uploading}>
                {folders.map((f) => (
                  <option key={f.path} value={f.path}>{f.path}</option>
                ))}
              </select>
            </label>
          </div>

          <div
            className={`dd-dropzone ${dragOver ? 'over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
            onClick={() => inputRef.current?.click()}
            role="button"
            tabIndex={0}
          >
            <p>Drop photos here or click to choose</p>
            <p className="dd-muted">JPEG, PNG, WebP, TIFF or SVG · up to 90 MB each · originals are kept at full resolution</p>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={ACCEPT}
              hidden
              onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
            />
          </div>

          {files.length > 0 && (
            <ul className="dd-upload-list">
              {files.map((entry, i) => (
                <li key={`${entry.file.name}-${i}`} className={entry.status}>
                  <span className="dd-upload-name">{entry.file.name}</span>
                  <span className="dd-muted">{formatBytes(entry.file.size)}</span>
                  <span className="dd-upload-status">
                    {entry.status === 'uploading' ? 'Uploading…' : entry.message || entry.status}
                  </span>
                </li>
              ))}
            </ul>
          )}

          <div className="dd-form">
            <label className="dd-inline-check">
              <input type="checkbox" checked={approved} onChange={(e) => setApproved(e.target.checked)} disabled={uploading} />
              Mark all as approved for external use
            </label>
            <label>
              Credit (applied to all)
              <input type="text" value={credit} onChange={(e) => setCredit(e.target.value)} disabled={uploading} />
            </label>
          </div>

          <div className="dd-detail-actions">
            <Button onClick={upload} loading={uploading} disabled={!folder || queued === 0}>
              Upload {queued > 0 ? `${queued} file${queued === 1 ? '' : 's'}` : ''}
            </Button>
            <Button variant="ghost" onClick={onClose} disabled={uploading}>
              Done
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default MediaUploadModal;
